import { create } from 'zustand'

import { FILTER_DEFAULT_SKIP, FILTER_DEFAULT_TAKE } from '@apps-common/config/filter'
import { UNIT_ORDER_DATE_DELETED, UNIT_ORDER_DEFAULT, UNIT_STATUS_ACTIVE, UNIT_STATUS_CANCELLED } from '@apps-common/config/unit-filter'
import { IPortalUnitSearchParamsModel } from '@apps-common/types/management/unit'

// Status
// MCentreId
// CompanyId,
// TechCompanyId
// Site
// AccountNo
// IMEI
// SerialNo
// MobileNo
// OrderBy
// PinnedUnits
// HasNoActivation
// Skip
// Take

export const DEFAULT_PORTAL_UNITS_FILTER_STORE_PROPS: IPortalUnitsFilterProps = {
  open: false,
  status: UNIT_STATUS_ACTIVE.value.toString(),
  mCentreId: '',
  bureauId: '',
  techCompanyId: '',
  networkType: '',
  site: '',
  accountNo: '',
  mobileNo: '',
  imei: '',
  serialNo: '',
  pinnedUnits: false,
  isActivated: true,
  hasNoActivation: false,
  skip: FILTER_DEFAULT_SKIP,
  take: FILTER_DEFAULT_TAKE,
  orderBy: UNIT_ORDER_DEFAULT.value.toString(),
  savedState: null
}

export interface IPortalUnitsFilterProps {
  open: boolean
  status: string
  mCentreId: string
  bureauId: string
  techCompanyId: string
  networkType: string
  site: string
  accountNo: string
  mobileNo: string
  imei: string
  serialNo: string
  pinnedUnits: boolean
  isActivated: boolean
  hasNoActivation: boolean
  skip: number
  take: number
  orderBy: string
  savedState: IPortalUnitsFilterProps | null
}

interface IPortalUnitsFilterState extends IPortalUnitsFilterProps {
  setOpen: (value: boolean) => void
  setStatus: (value: string) => void
  setMCentreId: (value: string) => void
  setCompanyId: (value: string) => void
  setTechCompanyId: (value: string) => void
  setNetworkType: (value: string) => void
  setSite: (value: string) => void
  setAccountNo: (value: string) => void
  setMobileNo: (value: string) => void
  setImei: (value: string) => void
  setSerialNo: (value: string) => void
  setOrderBy: (value: string) => void
  setIsActivated: (value: boolean) => void
  setPinnedUnits: (value: boolean) => void
  setSkip: (value: number) => void
  setTake: (value: number) => void
  getSearchParams: (skip: number, take: number) => IPortalUnitSearchParamsModel
  getFilteredByKeywords: () => string[]
  setTempState: (tempState: IPortalUnitsFilterProps) => void
  clear: () => void
}

export const usePortalUnitsFilterStore = create<IPortalUnitsFilterState>((set, get) => ({
  ...DEFAULT_PORTAL_UNITS_FILTER_STORE_PROPS,

  setOpen: (value: boolean) => set(() => ({ open: value })),
  setStatus: (value: string) => {
    const { status } = get()

    if (status !== value) {
      if (status === UNIT_STATUS_CANCELLED.value) {
        set(() => ({ status: value, skip: FILTER_DEFAULT_SKIP, orderBy: UNIT_ORDER_DATE_DELETED.value as string }))
      } else {
        set(() => ({ status: value, skip: FILTER_DEFAULT_SKIP, orderBy: UNIT_ORDER_DEFAULT.value as string }))
      }
    }
  },
  setMCentreId: (value: string) => {
    const { mCentreId } = get()

    if (mCentreId !== value) {
      set(() => ({ mCentreId: value }))
    }
  },
  setCompanyId: (value: string) => {
    const { bureauId } = get()

    if (bureauId !== value) {
      set(() => ({ bureauId: value }))
    }
  },
  setTechCompanyId: (value: string) => {
    const { techCompanyId } = get()

    if (techCompanyId !== value) {
      set(() => ({ techCompanyId: value }))
    }
  },
  setNetworkType: (value: string) => {
    const { networkType } = get()

    if (networkType !== value) {
      set(() => ({ networkType: value }))
    }
  },
  setSite: (value: string) => {
    const { site } = get()

    if (site !== value) {
      set(() => ({ site: value }))
    }
  },
  setAccountNo: (value: string) => {
    const { accountNo } = get()

    if (accountNo !== value) {
      set(() => ({ accountNo: value }))
    }
  },
  setMobileNo: (value: string) => {
    const { mobileNo } = get()

    if (mobileNo !== value) {
      set(() => ({ mobileNo: value }))
    }
  },
  setImei: (value: string) => {
    const { imei } = get()

    if (imei !== value) {
      set(() => ({ imei: value }))
    }
  },
  setSerialNo: (value: string) => {
    const { serialNo } = get()

    if (serialNo !== value) {
      set(() => ({ serialNo: value }))
    }
  },
  setOrderBy: (value: string) => {
    const { orderBy } = get()

    if (orderBy !== value) {
      set(() => ({ orderBy: value }))
    }
  },
  setIsActivated: (value: boolean) => {
    const { isActivated } = get()

    if (isActivated !== value) {
      set(() => ({ isActivated: value }))
    }
  },
  setPinnedUnits: (value: boolean) => {
    const { pinnedUnits } = get()

    if (pinnedUnits !== value) {
      set(() => ({ pinnedUnits: value }))
    }
  },
  setSkip: (value: number) => set(() => ({ skip: value })),
  setTake: (value: number) => set(() => ({ take: value })),
  getSearchParams: (skip: number, take: number) => {
    const {
      status,
      mCentreId,
      bureauId,
      techCompanyId,
      networkType,
      site,
      accountNo,
      mobileNo,
      imei,
      serialNo,
      orderBy,
      isActivated,
      hasNoActivation,
      pinnedUnits
    } = get()

    return {
      status,
      mCentreId,
      bureauId,
      techCompanyId,
      networkType,
      site,
      accountNo,
      mobileNo,
      imei,
      serialNo,
      orderBy,
      isActivated,
      hasNoActivation,
      pinnedUnits,
      skip,
      take
    }
  },
  getFilteredByKeywords: () => {
    const {
      status,
      mCentreId,
      bureauId,
      techCompanyId,
      networkType,
      site,
      accountNo,
      mobileNo,
      imei,
      serialNo,
      orderBy,
      isActivated,
      hasNoActivation,
      pinnedUnits
    } = get()

    const keywords = []

    if (status !== UNIT_STATUS_ACTIVE.value) {
      keywords.push('Status')
    }

    if (mCentreId?.length > 0) {
      keywords.push('MC')
    }

    if (bureauId?.length > 0) {
      keywords.push('Bureau')
    }

    if (techCompanyId?.length > 0) {
      keywords.push('Tech')
    }

    if (networkType?.length > 0) {
      keywords.push('Network')
    }

    if (site?.length > 0) {
      keywords.push('Site')
    }

    if (accountNo?.length > 0) {
      keywords.push('Acc')
    }

    if (mobileNo?.length > 0) {
      keywords.push('Mobile')
    }

    if (imei?.length > 0) {
      keywords.push('IMEI')
    }

    if (serialNo?.length > 0) {
      keywords.push('SerialNo')
    }

    return keywords
  },
  setTempState: (tempState: IPortalUnitsFilterProps) => {
    const {
      open,
      status,
      mCentreId,
      bureauId,
      techCompanyId,
      networkType,
      site,
      accountNo,
      mobileNo,
      imei,
      serialNo,
      hasNoActivation,
      pinnedUnits,
      skip,
      take,
      orderBy,
      isActivated,
      savedState
    } = get()

    set({
      ...tempState,
      savedState: {
        open,
        status,
        mCentreId,
        bureauId,
        techCompanyId,
        networkType,
        site,
        accountNo,
        mobileNo,
        imei,
        serialNo,
        hasNoActivation,
        pinnedUnits,
        skip,
        take,
        orderBy,
        isActivated,
        savedState
      }
    })
  },
  clear: (original?: boolean) => {
    const { savedState } = get()

    if (savedState && !original) {
      set(() => ({
        ...savedState
      }))
    } else {
      set(() => ({
        ...DEFAULT_PORTAL_UNITS_FILTER_STORE_PROPS
      }))
    }
  }
}))
